import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, MessageCircle } from 'lucide-react';
import { Button } from '../ui/Button';
const SLIDES = [{
  id: 1,
  /* Actual Maisha Medical Center clinic photo — team in front of the Maisha logo at reception */
  image: 'https://cdn.magicpatterns.com/uploads/2CDESMUURzyRTigZPfWppf/maisha_edited_3.jpg',
  headline: 'Your health is our devotion.',
  text: 'Proudly operating since 2013, Maisha Medical Center continues to offer accessible, affordable, and dependable medical services for individuals and families across Botswana.',
  buttons: [{
    text: 'Book Appointment',
    href: '/book',
    variant: 'primary',
    icon: <Calendar className="w-4 h-4" data-id="element-134" data-mp="zuwh5qvprxx4" />
  }, {
    text: 'WhatsApp Consultation',
    href: 'https://wa.me/26773241613',
    variant: 'white',
    icon: <MessageCircle className="w-4 h-4 text-[#25D366]" data-id="element-135" data-mp="uui948u44nz3" />
  }],
  highlights: ['Trusted Healthcare Professionals', 'Comprehensive Healthcare', 'Patient-Centered Care']
}, {
  id: 2,
  /* TODO: replace with actual Maisha clinic photo — Nursing and patient care image */
  image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&q=80&w=2000',
  headline: 'Your health is our devotion.',
  text: 'Comprehensive antenatal, postnatal, family planning, and child wellness services designed to support every stage of life.',
  buttons: [{
    text: 'Book Maternal Care',
    href: '/book?service=maternal',
    variant: 'primary',
    icon: <Calendar className="w-4 h-4" data-id="element-136" data-mp="1to30q1575ups" />
  }, {
    text: 'Speak To A Doctor',
    href: 'tel:+2676540244',
    variant: 'white'
  }],
  highlights: ['Antenatal Care', 'Postnatal Care', 'Family Planning', 'Child Wellness']
}, {
  id: 3,
  /* Actual Maisha Medical Center clinic photo — clinician performing an ultrasound on the Siemens diagnostic machine */
  image: 'https://cdn.magicpatterns.com/uploads/qwgKFzHJVLDNeBbjeP6Bin/20231219013809_IMG_0702-01-01.jpg',
  headline: 'Your health is our devotion.',
  text: 'Accurate laboratory testing, ECG assessments, ultrasound imaging, DNA testing, and medical examinations delivered with precision and care.',
  buttons: [{
    text: 'Book Diagnostic Service',
    href: '/book?service=diagnostic',
    variant: 'primary',
    icon: <Calendar className="w-4 h-4" data-id="element-137" data-mp="w9zzv0m1knhq" />
  }, {
    text: 'View Services',
    href: '/services',
    variant: 'white'
  }],
  highlights: ['Laboratory Services', 'ECG Services', 'Ultrasound', 'DNA Testing']
}, {
  id: 4,
  /* Actual Maisha community health screening photo */
  image: 'https://cdn.magicpatterns.com/uploads/sngGsJ6PjDsNpq161Wd5bP/slider_4.jpg',
  imagePosition: 'object-[center_35%]',
  headline: 'Your Health Matters. Our Community Cares.',
  text: 'We offer periodic free health screenings and wellness checks to promote healthier communities.',
  buttons: [{
    text: 'Learn About Our Community Initiatives',
    href: '/csr-initiatives',
    variant: 'primary'
  }],
  highlights: ['Free Health Screenings', 'Wellness Checks', 'Open to the Public']
}, {
  id: 5,
  /* Actual Maisha community outreach photo */
  image: 'https://cdn.magicpatterns.com/uploads/4DUJyG9sLUWiQQ34U5p6Jx/slider5.jpg',
  imagePosition: 'object-[center_25%]',
  headline: 'Caring Beyond Our Clinic',
  text: 'From Tsabong to Khawa, Middlepits, Omaoneno and Makopong, we bring community health screenings closer to the people who need them.',
  buttons: [{
    text: 'Discover Our Community Outreach',
    href: '/csr-initiatives#outreach',
    variant: 'primary'
  }],
  highlights: ['Tsabong', 'Khawa', 'Middlepits', 'Omaoneno', 'Makopong']
}];
export function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % SLIDES.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + SLIDES.length) % SLIDES.length);
  return <div className="relative h-[600px] md:h-[700px] lg:h-[800px] w-full overflow-hidden bg-brand-navy" data-id="element-138" data-mp="3jjlhedy9wjy2">
      <AnimatePresence mode="wait" data-id="element-139" data-mp="3emcvq3dx1xs2">
        <motion.div key={currentSlide} initial={{
        opacity: 0,
        scale: 1.05
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeInOut'
      }} className="absolute inset-0" data-id="element-140" data-mp="3ej752jpn2nu8">
          <div className="absolute inset-0 bg-brand-navy/60 z-10 mix-blend-multiply" data-id="element-141" data-mp="2zk3b8les7uq3" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/40 to-transparent z-10" data-id="element-142" data-mp="34fa9vet72985" />
          <img src={SLIDES[currentSlide].image} alt="Maisha Medical Center" className={`w-full h-full object-cover ${'imagePosition' in SLIDES[currentSlide] ? (SLIDES[currentSlide] as {
          imagePosition: string;
        }).imagePosition : 'object-center'}`} data-id="element-143" data-mp="2bspjohogg366" />

          <div className="absolute inset-0 z-20 flex items-center" data-id="element-144" data-mp="2lp109jv2vf6f">
            <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-145" data-mp="2qj58e6g2xt93">
              <div className="max-w-3xl" data-id="element-146" data-mp="1t150256av54w">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3,
                duration: 0.6
              }} data-id="element-147" data-mp="1t0di3x5ml5x3">
                  <span className="inline-block py-1 px-3 rounded-full bg-brand-red/20 text-brand-light border border-brand-red/30 text-sm font-semibold tracking-wider mb-6 backdrop-blur-sm" data-id="element-148" data-mp="1zpmtdilh9bcz">
                    MAISHA MEDICAL CENTER
                  </span>
                  <h1 className="text-4xl md:text-5xl lg:text-7xl font-heading font-bold text-white leading-tight mb-6 text-balance" data-id="element-149" data-mp="123ftgm04c76y">
                    {SLIDES[currentSlide].headline}
                  </h1>
                  <p className="text-lg md:text-xl text-slate-200 mb-10 max-w-2xl leading-relaxed text-balance" data-id="element-150" data-mp="1bzshp4cw9sub">
                    {SLIDES[currentSlide].text}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-151" data-mp="2ngsb63azadpx">
                    {SLIDES[currentSlide].buttons.map((btn, idx) => <Button key={idx} href={btn.href} variant={btn.variant as any} size="lg" icon={btn.icon} data-id="element-152" data-mp="2v1l17o91ypha" data-mpi={idx}>
                        {btn.text}
                      </Button>)}
                  </div>

                  <div className="flex flex-wrap gap-x-6 gap-y-3" data-id="element-153" data-mp="3dr160nrrt43i">
                    {SLIDES[currentSlide].highlights.map((highlight, idx) => <div key={idx} className="flex items-center gap-2 text-white/90 text-sm font-medium" data-id="element-154" data-mp="3iqxasp8akzk4" data-mpi={idx}>
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-gold" data-id="element-155" data-mp="38xgb54lh4q8j" />
                        {highlight}
                      </div>)}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Controls */}
      <div className="absolute bottom-8 right-4 sm:right-8 z-30 flex items-center gap-4" data-id="element-156" data-mp="37yhhnqz93ybl">
        <div className="flex gap-2 mr-4" data-id="element-157" data-mp="32z4nel61voyz">
          {SLIDES.map((_, idx) => <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-brand-red w-8' : 'bg-white/50 hover:bg-white'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-158" data-mp="32ws526wdrcle" data-mpi={idx} />)}
        </div>
        <button onClick={prevSlide} className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-brand-navy transition-colors backdrop-blur-sm" aria-label="Previous slide" data-id="element-159" data-mp="3lotajs3nu13s">
          <ChevronLeft className="w-6 h-6" data-id="element-160" data-mp="25bmbj1h3e6qr" />
        </button>
        <button onClick={nextSlide} className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-brand-navy transition-colors backdrop-blur-sm" aria-label="Next slide" data-id="element-161" data-mp="25a1ixhec8xvj">
          <ChevronRight className="w-6 h-6" data-id="element-162" data-mp="uqpzghvmrt4p" />
        </button>
      </div>
    </div>;
}